import React, { useCallback, useEffect, useRef, useState } from "react";

import { PATHS } from "../../cloud/config";
import { useCloud } from "../../cloud/context";
import {
  createFolder,
  deleteFile,
  listDirectory,
  openFile,
} from "../../cloud/storage";

import "./CloudFileBrowser.scss";

import type { FileNode } from "../../cloud/types";

interface Props {
  excalidrawAPI: any;
}

interface FolderState {
  [prefix: string]: FileNode[];
}

interface ExpandedState {
  [prefix: string]: boolean;
}

export const CloudFileBrowser: React.FC<Props> = ({ excalidrawAPI }) => {
  const {
    user,
    logout,
    setCurrentKey,
    setCurrentFilename,
    openSaveDialog,
    bindFile,
  } = useCloud();
  const [nodes, setNodes] = useState<FolderState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [loading, setLoading] = useState<{ [prefix: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    node: FileNode;
  } | null>(null);
  const [newFolderParent, setNewFolderParent] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  const personalRoot = PATHS.personal(user!.identityId);
  const sharedRoot = PATHS.shared();

  const loadDir = useCallback(async (prefix: string) => {
    setLoading((s) => ({ ...s, [prefix]: true }));
    try {
      const result = await listDirectory(prefix);
      setNodes((s) => ({ ...s, [prefix]: result }));
    } catch {
      setError("Failed to load files");
    } finally {
      setLoading((s) => ({ ...s, [prefix]: false }));
    }
  }, []);

  // Initial load of both roots
  useEffect(() => {
    loadDir(personalRoot);
    loadDir(sharedRoot);
    setExpanded({ [personalRoot]: true, [sharedRoot]: true });
  }, [personalRoot, sharedRoot, loadDir]);

  const toggleExpand = useCallback(
    (prefix: string) => {
      setExpanded((s) => {
        const next = !s[prefix];
        if (next && !nodes[prefix]) {
          loadDir(prefix);
        }
        return { ...s, [prefix]: next };
      });
    },
    [nodes, loadDir],
  );

  const handleOpenFile = useCallback(
    async (node: FileNode) => {
      try {
        const data = await openFile(node.key);
        // Bind autosave BEFORE updateScene — see OpenFromCloudDialog comment.
        await bindFile(node.key, data.etag);
        excalidrawAPI.updateScene({
          elements: data.elements,
          appState: {
            viewBackgroundColor: data.appState.viewBackgroundColor,
            // Clear any local-file handle so a prior local open doesn't linger.
            fileHandle: null,
          },
          collaborators: new Map(),
        });
        if (data.files && Object.keys(data.files).length > 0) {
          excalidrawAPI.addFiles(Object.values(data.files));
        }
        setCurrentKey(node.key);
        setCurrentFilename(node.name);
      } catch {
        setError("Failed to open file");
      }
    },
    [excalidrawAPI, setCurrentKey, setCurrentFilename, bindFile],
  );

  const handleDelete = useCallback(
    async (node: FileNode) => {
      if (!window.confirm(`Delete "${node.name}"?`)) {
        return;
      }
      try {
        await deleteFile(node.key);
        // Reload parent dir
        const parentPrefix = node.key.replace(node.name, "");
        await loadDir(parentPrefix);
      } catch {
        setError("Failed to delete");
      }
      setContextMenu(null);
    },
    [loadDir],
  );

  const startNewFolder = useCallback((prefix: string) => {
    setNewFolderParent(prefix);
    setNewFolderName("");
    setContextMenu(null);
    setTimeout(() => newFolderInputRef.current?.focus(), 50);
  }, []);

  const handleCreateFolder = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newFolderParent || !newFolderName.trim()) {
        return;
      }
      try {
        const prefix = `${newFolderParent}${newFolderName.trim()}/`;
        await createFolder(prefix);
        await loadDir(newFolderParent);
        setExpanded((s) => ({ ...s, [newFolderParent]: true }));
      } catch {
        setError("Failed to create folder");
      } finally {
        setNewFolderParent(null);
        setNewFolderName("");
      }
    },
    [newFolderParent, newFolderName, loadDir],
  );

  const renderNodes = (prefix: string, depth: number): React.ReactNode => {
    const items = nodes[prefix];
    if (!items) {
      return null;
    }

    return (
      <>
        {items.map((node) => (
          <div key={node.key} style={{ paddingLeft: `${depth * 12}px` }}>
            {node.type === "folder" ? (
              <div className="cloud-browser-row cloud-browser-folder">
                <button
                  className="cloud-browser-expand"
                  onClick={() => toggleExpand(node.key)}
                  aria-expanded={!!expanded[node.key]}
                >
                  {expanded[node.key] ? "▾" : "▸"}
                </button>
                <span
                  className="cloud-browser-name"
                  onClick={() => toggleExpand(node.key)}
                >
                  📁 {node.name}
                </span>
                <button
                  className="cloud-browser-action"
                  title="New folder inside"
                  onClick={() => startNewFolder(node.key)}
                >
                  +
                </button>
                <button
                  className="cloud-browser-action"
                  title="Save here"
                  onClick={() => openSaveDialog(node.key)}
                >
                  ↓
                </button>
              </div>
            ) : (
              <div
                className="cloud-browser-row cloud-browser-file"
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, node });
                }}
              >
                <span
                  className="cloud-browser-name"
                  onClick={() => handleOpenFile(node)}
                  title={node.name}
                >
                  📄 {node.name.replace(".excalidraw", "")}
                </span>
              </div>
            )}

            {/* Inline new folder input */}
            {newFolderParent === node.key && (
              <form
                onSubmit={handleCreateFolder}
                className="cloud-browser-newfolder"
                style={{ paddingLeft: `${(depth + 1) * 12}px` }}
              >
                <input
                  ref={newFolderInputRef}
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name"
                />
                <button type="submit">✓</button>
                <button type="button" onClick={() => setNewFolderParent(null)}>
                  ✕
                </button>
              </form>
            )}

            {/* Recursively render expanded folders */}
            {node.type === "folder" && expanded[node.key] && (
              <>
                {loading[node.key] ? (
                  <div
                    className="cloud-browser-loading"
                    style={{ paddingLeft: `${(depth + 1) * 12}px` }}
                  >
                    Loading…
                  </div>
                ) : (
                  renderNodes(node.key, depth + 1)
                )}
              </>
            )}
          </div>
        ))}

        {/* New folder input at root level */}
        {newFolderParent === prefix && (
          <form
            onSubmit={handleCreateFolder}
            className="cloud-browser-newfolder"
            style={{ paddingLeft: `${(depth + 1) * 12}px` }}
          >
            <input
              ref={newFolderInputRef}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
            />
            <button type="submit">✓</button>
            <button type="button" onClick={() => setNewFolderParent(null)}>
              ✕
            </button>
          </form>
        )}
      </>
    );
  };

  return (
    <div className="cloud-browser">
      {/* Header */}
      <div className="cloud-browser-header">
        <span className="cloud-browser-user">👤 {user?.username}</span>
        <button
          className="cloud-browser-logout"
          onClick={logout}
          title="Sign out"
        >
          Sign out
        </button>
      </div>

      {error && (
        <div
          className="cloud-browser-error"
          onClick={() => setError(null)}
          role="alert"
        >
          {error} ✕
        </div>
      )}

      {/* Personal section */}
      <div className="cloud-browser-section">
        <div className="cloud-browser-section-header">
          <button
            className="cloud-browser-section-toggle"
            onClick={() => toggleExpand(personalRoot)}
          >
            {expanded[personalRoot] ? "▾" : "▸"} My Files
          </button>
          <button
            className="cloud-browser-action"
            title="New folder"
            onClick={() => startNewFolder(personalRoot)}
          >
            +
          </button>
          <button
            className="cloud-browser-action"
            title="Save here"
            onClick={() => openSaveDialog(personalRoot)}
          >
            ↓
          </button>
        </div>

        {expanded[personalRoot] && (
          <div className="cloud-browser-tree">
            {loading[personalRoot] ? (
              <span className="cloud-browser-loading">Loading…</span>
            ) : (
              renderNodes(personalRoot, 0)
            )}
          </div>
        )}
      </div>

      {/* Shared section */}
      <div className="cloud-browser-section">
        <div className="cloud-browser-section-header">
          <button
            className="cloud-browser-section-toggle"
            onClick={() => toggleExpand(sharedRoot)}
          >
            {expanded[sharedRoot] ? "▾" : "▸"} Shared
          </button>
          <button
            className="cloud-browser-action"
            title="New folder"
            onClick={() => startNewFolder(sharedRoot)}
          >
            +
          </button>
          <button
            className="cloud-browser-action"
            title="Save here"
            onClick={() => openSaveDialog(sharedRoot)}
          >
            ↓
          </button>
        </div>

        {expanded[sharedRoot] && (
          <div className="cloud-browser-tree">
            {loading[sharedRoot] ? (
              <span className="cloud-browser-loading">Loading…</span>
            ) : (
              renderNodes(sharedRoot, 0)
            )}
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <>
          <div
            className="cloud-browser-backdrop"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="cloud-browser-context"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            <button onClick={() => handleOpenFile(contextMenu.node)}>
              Open
            </button>
            <button onClick={() => handleDelete(contextMenu.node)}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};
